<template>
  <div :class="cssClasses">
    <input
      ref="ctaFileRef"
      class="file-selection__input"
      type="file"
      accept="image/png, image/jpeg, image/jpg, application/pdf"
      :disabled="ctrlState === 'DISABLED'"
    />
    <div v-if="state === FILE_STATES.DEFAULT">
      <div class="file-selection__choose-ctrl">
        <BaseButton
          :disabled="interactiveButtonState === 'DISABLED'"
          :text="selectionChooseCtrlText"
          small
          data-qa="file-select-submit"
          @click="handleFileSelectionCTAButtonClick"
        />
      </div>
      <div
        class="file-selection__hint"
        data-qa="hint"
      >
        {{ selectionHint }}
      </div>
    </div>
    <div v-else-if="state === FILE_STATES.PROCESSING">
      <div class="file-selection__spinner">
        <Base64Spinner />
      </div>
      <div class="file-selection__hint">{{ processingProgress }}%</div>
    </div>
    <div v-else>
      <div
        v-if="selectionPreview"
        ref="previewFileRef"
        class="file-selection__preview"
      />
      <div :class="nameCSSClasses">
        {{ selectedFileName() }}
      </div>
      <button
        type="button"
        :disabled="ctrlState === 'DISABLED'"
        :class="removeCtrlCSSClasses"
        @click="handleRemoveFileClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseButton from '@shared/components/BaseButton.vue'

const FILE_STATES = {
  DEFAULT: 'DEFAULT',
  PROCESSING: 'PROCESSING',
  SELECTED: 'SELECTED',
}

const CTRLS_STATES = {
  DEFAULT: 'DEFAULT',
  DISABLED: 'DISABLED',
}

const props = defineProps({
  selectionHint: {
    type: String,
    default: '',
  },
  selectionChooseCtrlText: {
    type: String,
    default: '',
  },
  selectionPreview: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['reset', 'select'])

const ctaFileRef = ref(null)
const previewFileRef = ref(null)
const ctrlState = ref(CTRLS_STATES.DEFAULT)
const state = ref(FILE_STATES.DEFAULT)
const processingProgress = ref(0)
const cssClasses = ref('file-selection')

const interactiveButtonState = computed(() =>
  ctrlState.value === CTRLS_STATES.DISABLED ? 'DISABLED' : 'ACTIVE',
)

const removeCtrlCSSClasses = computed(() =>
  ctrlState.value === CTRLS_STATES.DISABLED
    ? 'file-selection__remove-ctrl file-selection__remove-ctrl--disabled'
    : 'file-selection__remove-ctrl',
)

const nameCSSClasses = computed(() =>
  ctrlState.value === CTRLS_STATES.DISABLED
    ? 'file-selection__name file-selection__name--disabled'
    : 'file-selection__name',
)

const selectedFileName = () =>
  ctaFileRef.value.files.length > 0 ? ctaFileRef.value.files[0].name : ''

const handleFileSelectionCTAButtonClick = () => {
  ctaFileRef.value.click()
}

const handleRemoveFileClick = () => {
  state.value = FILE_STATES.DEFAULT
  ctaFileRef.value.value = ''
  emit('reset')

  if (props.selectionPreview) {
    previewFileRef.value.innerHTML = ''
    cssClasses.value = 'file-selection'
  }
}

const handleFileSelection = (e) => {
  state.value = FILE_STATES.SELECTED
  emit('select', e.target.files)

  if (props.selectionPreview) {
    previewImageUrl()
    cssClasses.value = 'file-selection file-selection--preview'
  }
}

const previewImageUrl = () => {
  const files = ctaFileRef.value.files
  if (!files.length) {
    return
  }

  if (files[0].type === 'application/pdf') {
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    reader.onload = null
    const img = document.createElement('img')
    img.style.cssText = 'max-width: 100%; max-height: 320px;'
    img.src = reader.result
    previewFileRef.value.appendChild(img)
  }
  reader.readAsDataURL(files[0])
}

const nextState = ({ fileSelection, fileProcessingProgress }) => {
  if (fileSelection in FILE_STATES) {
    state.value = fileSelection
  }
  if (
    typeof fileProcessingProgress === 'number' &&
    !isNaN(fileProcessingProgress)
  ) {
    processingProgress.value = fileProcessingProgress
  }
}

const nextCtrlState = ({ state }) => {
  if (state in CTRLS_STATES) {
    ctrlState.value = state
  }
}

onMounted(() => {
  ctaFileRef.value.addEventListener('change', handleFileSelection)
})

onBeforeUnmount(() => {
  ctaFileRef.value.removeEventListener('change', handleFileSelection)
})

defineExpose({
  nextState,
  nextCtrlState,
  FILE_STATES,
})
</script>

<style lang="scss">
.file-selection {
  position: relative;
  text-align: center;
  background-color: $kds-color-background-subtle;
  border: 1px dashed #7796b8;
  border-radius: 8px;
  padding: 16px 8px;

  &--preview {
    border-style: solid;
  }

  &__title {
    font-weight: 700;
  }

  &__hint {
    @include kds-typography-paragraph-medium;
    margin-top: 16px;
  }

  &__spinner {
    width: 54px;
    height: 54px;
    margin: 0 auto;
  }

  &__input {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    height: 1px;
    width: 1px;
  }

  &__name {
    font-weight: 500;
    color: $kds-color-text-primary;
    display: inline-block;
    vertical-align: middle;
    max-width: 75%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &--disabled {
      color: $kds-color-text-primary;
      opacity: 0.5;
    }
  }

  &__choose-ctrl {
    width: 75%;
    margin: 0 auto;
  }

  &__remove-ctrl {
    border: none;
    padding: 20px;
    vertical-align: middle;
    background: url('@shared/assets/img/icon-remove-midnight.svg') center center
      no-repeat;
    background-size: 50%;
    cursor: pointer;

    &--disabled {
      cursor: default;
      opacity: 0.5;
    }
  }

  &--error {
    border-color: $kds-color-background-interactive-destructive;
  }

  @include media-up(md) {
    &__choose-ctrl {
      display: flex;
      justify-content: center;
      width: 50%;
    }
  }
}
</style>
